import { HStack, Tabs, VStack } from '@chakra-ui/react';
import {
  LADFilters,
  PerimeterFilters,
  TechnicalFilters,
} from '@components/cockpit/panels/notificationSearch/categoryFilters';
import {
  NotificationSearchContextProvider,
  useNotificationSearchContext,
} from '@components/cockpit/panels/notificationSearch/NotificationSearchContext';
import { SearchFilterOptions } from '@components/cockpit/panels/notificationSearch/SearchFilterOptions';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationFamilyEnum } from '@/types/data/data.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import Panel from '../Panel';
import { SearchFilterTriggerPanel } from './filterComponents/SearchFilterTriggerPanel';
import { NotificationSearchList } from './NotificationSearchList';
import NotificationSearchReporting from './reporting/NotificationSearchReporting';

function FiltersPanelTabs() {
  const { resetFilters, activeTab, setActiveTab } = useNotificationSearchContext();

  return (
    <Tabs.Root variant="filter" value={activeTab}>
      <Tabs.List>
        <Tabs.Trigger
          flex={1}
          value={NotificationFamilyEnum.LAD}
          onClick={() => {
            resetFilters(NotificationFamilyEnum.LAD);
            setActiveTab(NotificationFamilyEnum.LAD);
          }}
        >
          <FormattedMessage id="components.notificationSearch.filters.categories.types.LAD" />
        </Tabs.Trigger>
        <Tabs.Trigger
          flex={1}
          value={NotificationFamilyEnum.PERIMETER}
          onClick={() => {
            resetFilters(NotificationFamilyEnum.PERIMETER);
            setActiveTab(NotificationFamilyEnum.PERIMETER);
          }}
        >
          <FormattedMessage id="components.notificationSearch.filters.categories.types.PERIMETER" />
        </Tabs.Trigger>
        <Tabs.Trigger
          flex={1}
          value={NotificationFamilyEnum.TECHNICAL}
          onClick={() => {
            resetFilters(NotificationFamilyEnum.TECHNICAL);
            setActiveTab(NotificationFamilyEnum.TECHNICAL);
          }}
        >
          <FormattedMessage id="components.notificationSearch.filters.categories.types.TECHNICAL" />
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.ContentGroup>
        <Tabs.Content value={NotificationFamilyEnum.LAD}>
          <LADFilters />
        </Tabs.Content>
        <Tabs.Content value={NotificationFamilyEnum.PERIMETER}>
          <PerimeterFilters />
        </Tabs.Content>
        <Tabs.Content value={NotificationFamilyEnum.TECHNICAL}>
          <TechnicalFilters />
        </Tabs.Content>
      </Tabs.ContentGroup>
    </Tabs.Root>
  );
}

export const DEFAULT_PAGE_SIZE = 30;

function NotificationSearchPanelContent() {
  const { formatMessage } = useIntl();

  const filtersPanelRef = useRef<HTMLDivElement | null>(null);
  const { openedFilterOptions, openedReporting, setOpenedFilterOptions, activeSearchFilterField } =
    useNotificationSearchContext();

  return (
    <Panel
      isEmpty={false}
      header={<CockpitPanelHeader label={formatMessage({ id: 'components.notificationSearch.title' })} />}
    >
      <HStack width="100%" height="100%" backgroundColor="neutral.black" flexShrink={0} gap={0.25} userSelect="none">
        <HStack width="400px" height="100%" gap={0} alignItems="start" backgroundColor="neutral.black">
          <VStack ref={filtersPanelRef} width="100%" height="100%" alignItems="start">
            {activeSearchFilterField ? (
              <SearchFilterTriggerPanel field={activeSearchFilterField} />
            ) : (
              <FiltersPanelTabs />
            )}
          </VStack>
          {openedFilterOptions && (
            <SearchFilterOptions parentRef={filtersPanelRef} onClose={() => setOpenedFilterOptions(false)} />
          )}
        </HStack>
        {openedReporting ? <NotificationSearchReporting /> : <NotificationSearchList />}
      </HStack>
    </Panel>
  );
}

export function NotificationSearchPanel() {
  return (
    <NotificationSearchContextProvider>
      <NotificationSearchPanelContent />
    </NotificationSearchContextProvider>
  );
}
